<template>
  <div class="detail-panel w-80 bg-white shrink-0 flex flex-col overflow-hidden border-l border-slate-200/60">
    <!-- 头部 -->
    <div class="panel-header px-4 py-3 border-b border-slate-100/80 flex items-center justify-between shrink-0 bg-white">
      <span class="text-sm font-semibold text-slate-700 truncate">素材详情</span>
      <el-button text size="small" class="!w-7 !h-7" @click="$emit('close')">
        <el-icon :size="16"><Close /></el-icon>
      </el-button>
    </div>

    <div class="panel-body flex-1 overflow-y-auto">
      <!-- 预览区域 -->
      <div class="preview-area bg-gradient-to-br from-slate-100 to-slate-50 flex items-center justify-center border-b border-slate-100/60" :class="previewHeightClass">
        <!-- 图片 -->
        <div v-if="asset.type === 'IMAGE'" class="w-full h-full flex items-center justify-center text-slate-300 p-2">
          <img
            v-if="asset.coverUrl"
            :src="asset.coverUrl"
            :alt="asset.name"
            class="max-w-full max-h-full object-contain rounded shadow-sm"
          />
          <el-icon v-else :size="48"><Picture /></el-icon>
        </div>
        <!-- 视频 -->
        <div v-else-if="asset.type === 'VIDEO'" class="w-full h-full flex items-center justify-center text-slate-400 bg-black/5">
          <video
            v-if="videoPreviewUrl"
            :key="videoPreviewUrl"
            :src="videoPreviewUrl"
            :poster="asset.coverUrl || undefined"
            class="w-full h-full object-contain"
            controls
            preload="metadata"
          >
            当前浏览器不支持视频播放
          </video>
          <div v-else class="flex flex-col items-center px-4 text-center">
            <el-icon :size="40"><VideoPlay /></el-icon>
            <span class="text-xs mt-2 text-slate-400">{{ videoPreviewLoading ? '加载视频预览中' : '暂无可播放预览' }}</span>
          </div>
        </div>
        <!-- 文件 -->
        <div v-else-if="asset.type === 'FILE'" class="flex flex-col items-center text-slate-400">
          <div class="w-16 h-16 rounded-2xl bg-sky-50 flex items-center justify-center mb-2">
            <el-icon :size="40"><Document /></el-icon>
          </div>
          <span class="text-xs mt-1 text-slate-600 font-medium">{{ currentVersion?.originalName || asset.name }}</span>
          <span class="text-[10px] text-slate-400 mt-1" v-if="currentVersion?.fileSize">
            {{ formatFileSize(currentVersion.fileSize) }}
          </span>
        </div>
        <!-- 文本 -->
        <div v-else-if="asset.type === 'TEXT'" class="w-full h-full p-4 overflow-y-auto bg-white">
          <p class="text-sm text-slate-600 whitespace-pre-wrap leading-relaxed">{{ textPreview }}</p>
        </div>
      </div>

      <!-- 基本信息 -->
      <div class="info-section px-4 py-4 border-b border-slate-100/60">
        <h4 class="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-3">基本信息</h4>
        <p class="text-sm font-semibold text-slate-800 mb-1">{{ asset.name }}</p>
        <p class="text-xs text-slate-500 mb-3 leading-relaxed" v-if="asset.description">{{ asset.description }}</p>
        <div class="flex flex-wrap gap-1.5 mb-3">
          <span
            v-for="tag in asset.tags"
            :key="tag.id"
            class="text-[10px] px-2 py-1 rounded-md font-medium"
            :style="{ backgroundColor: (tag.color || '#6366f1') + '15', color: tag.color || '#6366f1' }"
          >
            {{ tag.name }}
          </span>
          <span v-if="!asset.tags?.length" class="text-[10px] text-slate-300">无标签</span>
        </div>
        <div class="text-[11px] text-slate-400 space-y-1.5 bg-slate-50/60 rounded-lg p-2.5">
          <div class="flex items-center gap-2">
            <span class="text-slate-400 w-10 shrink-0">类型</span>
            <span class="text-slate-600 font-medium">{{ typeLabel(asset.type) }}</span>
          </div>
          <div v-if="currentVersion?.fileSize" class="flex items-center gap-2">
            <span class="text-slate-400 w-10 shrink-0">大小</span>
            <span class="text-slate-600 font-medium">{{ formatFileSize(currentVersion.fileSize) }}</span>
          </div>
          <div v-if="currentVersion?.width && currentVersion?.height" class="flex items-center gap-2">
            <span class="text-slate-400 w-10 shrink-0">尺寸</span>
            <span class="text-slate-600 font-medium">{{ currentVersion.width }} × {{ currentVersion.height }}</span>
          </div>
          <div class="flex items-center gap-2">
            <span class="text-slate-400 w-10 shrink-0">创建</span>
            <span class="text-slate-500">{{ formatTime(asset.createTime) }}</span>
          </div>
          <div class="flex items-center gap-2">
            <span class="text-slate-400 w-10 shrink-0">更新</span>
            <span class="text-slate-500">{{ formatTime(asset.updateTime) }}</span>
          </div>
        </div>
        <div class="flex items-center gap-1 mt-3">
          <el-button text size="small" class="!text-xs !px-2 !py-1 text-slate-500 hover:text-primary-600 hover:bg-primary-50/50 transition-colors" @click="handleCurrentPreview">
            <el-icon class="mr-1"><View /></el-icon>预览
          </el-button>
          <el-button text size="small" class="!text-xs !px-2 !py-1 text-slate-500 hover:text-primary-600 hover:bg-primary-50/50 transition-colors" @click="handleCurrentDownload">
            <el-icon class="mr-1"><Download /></el-icon>下载
          </el-button>
        </div>
      </div>

      <!-- 历史版本 -->
      <div class="versions-section px-4 py-4">
        <div class="flex items-center justify-between mb-3">
          <h4 class="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
            版本历史 <span class="text-primary-600 font-bold ml-1">{{ versions.length || 0 }}</span>
          </h4>
          <el-button
            v-if="folderManageable"
            text
            size="small"
            type="primary"
            class="!text-xs !h-7"
            @click="$emit('new-version', asset)"
          >
            <el-icon class="mr-0.5"><Plus /></el-icon>新增
          </el-button>
        </div>

        <div v-if="!versions.length && !versionsLoading" class="text-xs text-slate-300 text-center py-6 bg-slate-50/40 rounded-lg">
          暂无版本记录
        </div>

        <div v-loading="versionsLoading" class="space-y-2">
          <div
            v-for="v in versions"
            :key="v.id"
            class="version-item rounded-lg p-3 border transition-all duration-150"
            :class="v.id === asset.currentVersionId ? 'border-primary-200/80 bg-primary-50/30 shadow-sm' : 'border-slate-100 bg-white hover:border-slate-200 hover:shadow-sm'"
          >
            <div class="flex items-center justify-between mb-1.5">
              <span class="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                <span class="w-5 h-5 rounded bg-slate-100 flex items-center justify-center text-[10px] font-bold text-slate-500">v</span>
                {{ v.versionNo }}
                <span v-if="v.id === asset.currentVersionId" class="text-[10px] px-1.5 py-0.5 rounded-md bg-primary-100 text-primary-700 font-medium">当前</span>
              </span>
              <span class="text-[10px] text-slate-400">{{ formatTime(v.createTime) }}</span>
            </div>
            <p class="text-[11px] text-slate-500 mb-2 leading-relaxed" v-if="v.changeRemark">{{ v.changeRemark }}</p>
            <div class="flex items-center gap-1.5" v-if="v.id !== asset.currentVersionId && folderManageable">
              <el-button
                text
                size="small"
                type="primary"
                class="!text-xs !px-2 !py-1 h-7 bg-primary-50/60 hover:bg-primary-100/80 transition-colors"
                @click="$emit('restore-version', { assetId: asset.id, versionId: v.id })"
              >
                恢复此版本
              </el-button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部操作 -->
    <div class="panel-footer px-2 py-2 border-t border-slate-100/80 shrink-0 bg-white" v-if="folderManageable">
      <div class="flex justify-between gap-0.5">
        <el-button size="small" class="!px-1" @click="$emit('edit', asset)">
          <el-icon class="mr-2"><Edit /></el-icon>编辑
        </el-button>
        <el-button size="small" class="!px-1" @click="$emit('move', asset)">
          <el-icon class="mr-2"><Right /></el-icon>移动
        </el-button>
        <el-button size="small" class="!px-1" @click="$emit('tags', asset)">
          <el-icon class="mr-2"><PriceTag /></el-icon>标签
        </el-button>
        <el-button size="small" type="danger" class="!px-1" @click="$emit('delete', asset)">
          <el-icon class="mr-2"><Delete /></el-icon>删除
        </el-button>
      </div>
    </div>

    <!-- 通用文件预览弹窗 -->
    <FilePreviewModal v-model="previewVisible" :file="previewFile" />
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { Close, VideoPlay, Document, Picture, View, Download, Edit, Right, PriceTag, Delete, Plus } from '@element-plus/icons-vue'
import { previewAsset, downloadAsset } from '@/api/assets'
import FilePreviewModal from '@/components/FilePreviewModal.vue'

const props = defineProps({
  asset: { type: Object, default: null },
  versions: { type: Array, default: () => [] },
  versionsLoading: { type: Boolean, default: false },
  folderManageable: { type: Boolean, default: false }
})

defineEmits(['close', 'edit', 'delete', 'move', 'tags', 'new-version', 'restore-version'])

const currentVersion = computed(() => {
  return props.versions?.find(v => v.id === props.asset?.currentVersionId) || null
})

const videoPreviewUrl = ref('')
const videoPreviewLoading = ref(false)

const previewVisible = ref(false)
const previewFile = ref(null)

const textPreview = computed(() => {
  const content = currentVersion.value?.textContent || ''
  return content.length > 500 ? content.substring(0, 500) + '...' : content
})

const previewHeightClass = computed(() => {
  if (props.asset?.type === 'TEXT') return 'h-36'
  if (props.asset?.type === 'VIDEO') return 'h-48'
  return 'h-44'
})

watch(
  () => [props.asset?.id, props.asset?.type],
  () => {
    loadVideoPreview()
  },
  { immediate: true }
)

function typeLabel(type) {
  const map = { IMAGE: '图片', VIDEO: '视频', FILE: '文件', TEXT: '文本' }
  return map[type] || type
}

function formatFileSize(bytes) {
  if (!bytes) return ''
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / 1048576).toFixed(1) + ' MB'
}

function formatTime(time) {
  if (!time) return ''
  return time.replace('T', ' ')
}

function openTextContent(content) {
  const blob = new Blob([content || ''], { type: 'text/plain;charset=UTF-8' })
  const url = URL.createObjectURL(blob)
  window.open(url, '_blank')
  window.setTimeout(() => URL.revokeObjectURL(url), 30000)
}

async function loadVideoPreview() {
  videoPreviewUrl.value = ''
  if (props.asset?.type !== 'VIDEO' || !props.asset?.id) {
    return
  }
  videoPreviewLoading.value = true
  try {
    const data = await previewAsset(props.asset.id)
    videoPreviewUrl.value = typeof data === 'string' ? '' : (data?.previewUrl || '')
  } finally {
    videoPreviewLoading.value = false
  }
}

async function handleCurrentPreview() {
  const assetType = props.asset?.type
  // TEXT / VIDEO：通用预览组件不支持，保留原行为
  if (assetType === 'TEXT' || assetType === 'VIDEO') {
    const data = await previewAsset(props.asset.id)
    if (typeof data === 'string') {
      openTextContent(data)
      return
    }
    if (data?.previewUrl) {
      window.open(data.previewUrl, '_blank')
    }
    return
  }

  // IMAGE / FILE：使用通用预览组件
  const data = await previewAsset(props.asset.id)
  if (typeof data === 'string') {
    openTextContent(data)
    return
  }
  if (!data?.previewUrl) return
  const v = currentVersion.value
  previewFile.value = {
    url: data.previewUrl,
    name: v?.originalName || props.asset.name,
    originalName: v?.originalName,
    fileName: v?.originalName,
    size: v?.fileSize,
    fileSize: v?.fileSize,
    contentType: v?.contentType
  }
  previewVisible.value = true
}

async function handleCurrentDownload() {
  const data = await downloadAsset(props.asset.id)
  if (typeof data === 'string') {
    openTextContent(data)
    return
  }
  if (data?.downloadUrl) {
    window.open(data.downloadUrl, '_blank')
  }
}
</script>
